import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect, useCallback } from 'react';
import { videoFeedbackService, VideoFeedbackConfig, FeedbackMetrics, PaginatedResponses } from '@/services/videoFeedbackService';
import { useToast } from '@/hooks/use-toast';

interface UseVideoFeedbackOptions {
  videoId: string;
  enabled?: boolean;
}

interface UseVideoFeedbackReturn {
  config: VideoFeedbackConfig | null;
  metrics: FeedbackMetrics | null;
  responses: PaginatedResponses | null;
  isLoading: boolean;
  isLoadingMetrics: boolean;
  error: string | null;
  updateConfig: (config: Partial<VideoFeedbackConfig>) => Promise<void>;
  refetchConfig: () => Promise<void>;
  refetchMetrics: (inicio: string, fim: string) => Promise<void>;
  fetchResponses: (page?: number, limit?: number, filter?: string, search?: string) => Promise<void>;
  exportData: (formato?: 'csv' | 'excel', inicio?: string, fim?: string) => Promise<void>;
}

export function useVideoFeedback({
  videoId,
  enabled = true,
}: UseVideoFeedbackOptions): UseVideoFeedbackReturn {
  useLanguage();
  const { toast } = useToast();

  const [config, setConfig] = useState<VideoFeedbackConfig | null>(null);
  const [metrics, setMetrics] = useState<FeedbackMetrics | null>(null);
  const [responses, setResponses] = useState<PaginatedResponses | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetchConfig = useCallback(async () => {
    if (!videoId || !enabled) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await videoFeedbackService.getConfig(videoId);
      setConfig(data.videoFeedbackConfig);
    } catch (err) {
      const message = err instanceof Error ? err.message : translateInterfaceText("Erro ao carregar configuração");
      setError(message);
      toast({
        title: translateInterfaceText("Erro"),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [videoId, enabled, toast]);

  const refetchMetrics = useCallback(async (inicio: string, fim: string) => {
    if (!videoId || !enabled) return;

    setIsLoadingMetrics(true);

    try {
      const data = await videoFeedbackService.getMetrics(videoId, inicio, fim);
      setMetrics(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : translateInterfaceText("Erro ao carregar métricas");
      toast({
        title: translateInterfaceText("Erro"),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsLoadingMetrics(false);
    }
  }, [videoId, enabled, toast]);

  const fetchResponses = useCallback(async (
    page: number = 1,
    limit: number = 20,
    filter?: string,
    search?: string
  ) => {
    if (!videoId || !enabled) return;

    try {
      const data = await videoFeedbackService.listResponses(
        videoId,
        page,
        limit,
        filter,
        search
      );
      setResponses(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : translateInterfaceText("Erro ao carregar respostas");
      toast({
        title: translateInterfaceText("Erro"),
        description: message,
        variant: 'destructive',
      });
    }
  }, [videoId, enabled, toast]);

  const updateConfig = useCallback(async (newConfig: Partial<VideoFeedbackConfig>) => {
    // A atualização real da config será feita através do hook usePersonalization
    // que gerencia o estado global do vídeo
    setConfig((prev) => prev ? { ...prev, ...newConfig } : null);
  }, []);

  const exportData = useCallback(async (
    formato: 'csv' | 'excel' = 'csv',
    inicio?: string,
    fim?: string
  ) => {
    if (!videoId) return;

    try {
      const blob = await videoFeedbackService.exportData(videoId, formato, inicio, fim);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `feedback_${videoId}_${Date.now()}.${formato}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: translateInterfaceText("Sucesso"),
        description: translateInterfaceText("Arquivo exportado com sucesso"),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : translateInterfaceText("Erro ao exportar dados");
      toast({
        title: translateInterfaceText("Erro"),
        description: message,
        variant: 'destructive',
      });
    }
  }, [videoId, toast]);

  useEffect(() => {
    if (videoId && enabled) {
      refetchConfig();
    }
  }, [videoId, enabled, refetchConfig]);

  return {
    config,
    metrics,
    responses,
    isLoading,
    isLoadingMetrics,
    error,
    updateConfig,
    refetchConfig,
    refetchMetrics,
    fetchResponses,
    exportData,
  };
}
